// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { TabProps } from "@insite/mobius/Tab";
import getColor from "@insite/mobius/utilities/getColor";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { xBaseTheme } from "@mcmc/XDL/types";
import { onPrint, printStyles } from "@mcmc/XDL/Utilities";
import { RobotoCondensed } from "@mcmc/XDL/Utilities/sharedStyles";
import { css } from "styled-components";

export default {
    defaultProps: {
        typographyProps: {
            size: "16px",
            lineHeight: "20px",
            ...RobotoCondensed,
            css: css`
                letter-spacing: 0.18px;
                text-transform: uppercase;
            `,
        },
        css: css`
            border: 1px solid ${getColor("common.border")};
            background: ${getColor("common.ui3")};
            padding: 8px 10px;
            min-width: 100px;
            
            & + & {
                margin-left: 10px;
            }
            transition: box-shadow 0.2s, font-weight 0.2s;

            [role="tab"]:before {
                content: "${({ headline }: any) => headline}";
                display: block;
                font-weight: 700;
                height: 0;
                overflow: hidden;
                visibility: hidden;
            }

            ${({ selected, theme }: TabProps) => {
                const activeStyles = css`
                    background: ${resolveColor("primary.contrast", theme!)};
                    padding-top: 8px;
                    border-color: ${resolveColor("primary", theme!)} ${resolveColor("common.border", theme!)}
                        transparent ${resolveColor("common.border", theme!)};
                    border-style: solid;
                    border-width: 3px 1px 1px 1px;
                    font-weight: 700;
                    outline: none;
                    z-index: 1;
                `;

                return css`
                    &:hover {
                        font-weight: 700;
                        border: 1px solid ${resolveColor("common.border", theme!)};
                        cursor: pointer;
                    }

                    &:focus {
                        background: ${resolveColor("text.inverse", theme!)};
                        box-shadow: 0 0 4px 0 rgba(33, 33, 33, 0.5);
                        ${activeStyles}
                    }

                    ${selected &&
                    css`
                        &,
                        &:hover {
                            background: linear-gradient(
                                180deg,
                                ${resolveColor("common.background", theme!)} 0%,
                                ${resolveColor("text.inverse", theme!)} 100%
                            );
                            cursor: auto;
                            ${activeStyles}
                        }
                    `}
                `;
            }};
        `,
    },
    groupDefaultProps: {
        cssOverrides: {
            wrapper: css`
                padding: 0;
            `,
            tabGroup: css`
                padding: 0;
                z-index: auto;
                align-items: flex-end;
                flex: 0 0 auto;
                ${printStyles.hideOnPrint}
            `,
            tabContent: css`
                margin-top: -1px;
                border-top: 1px solid ${getColor("common.border")};
                border-bottom: none;
                padding: 10px 0;

                ${onPrint(css`
                    display: block !important;
                    border: 1px solid ${getColor("common.ui1")};
                    padding: 10px;
                    margin-bottom: 20px;
                `)}
                ${printStyles.avoidBreakInside}
            `,
        },
    },
} as xBaseTheme["tab"];
